import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import apiClient from "../../../services/apiClient";
import { InputField, MultiSelectField, SelectField } from "../Petowner/AddOwnerModal";
import { SelectChangeEvent } from "@mui/material";
import moment from "moment";
import axios from "axios";

export default function AddVetetinarianModal({ onClose, onSuccess, editData, mode = "add", }: {
    onClose: () => void; onSuccess?: () => void; editData?: any; mode?: "add" | "edit";
}) {

    const coreFields = [
        "firstName", "lastName", "gender", "mobile", "email",
        "username", "password", "confirmPassword",
    ];

    const vetFields = [
        "qualification", "license_no", "aadhar_no", "dob", "blood_group",
        "practising_since", "speciality", "isd_code"
    ];

    const bankFields = ["bank_name", "bank_ifsc", "bank_account_number"];

    const fieldKeys = [...coreFields, ...vetFields, ...bankFields];

    const createInitialState = (keys: string[]) =>
        Object.fromEntries(keys.map((key) => [key, ""])) as Record<string, string | string[]>;

    const initialFormState = createInitialState(fieldKeys);

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState<Record<string, string>>({});
    type OptionBase = { label: string; value: string };
    type SpecialityOption = OptionBase & { id?: number; slug?: string };
    const [specialityOptions, setSpecialityOptions] = useState<SpecialityOption[]>([]);
    const [bankOptions, setBankOptions] = useState<OptionBase[]>([]);

    const [ifscDetails, setIfscDetails] = useState<any>(null);
    const [ifscLoading, setIfscLoading] = useState(false);
    const [isBankVerified, setIsBankVerified] = useState(false);
    const [isBankDisabled, setIsBankDisabled] = useState(false);

    useEffect(() => {
        const masterConfigs = [
            { slug: "speciality", setter: setSpecialityOptions },
            { slug: "bank_name", setter: setBankOptions },
        ];

        const fetchMasters = async () => {
            try {
                const results = await Promise.all(
                    masterConfigs.map(async ({ slug, setter }) => {
                        const res: any = await apiClient(`/v1/users/get-master-x?slug=${slug}&order_name=name&dir=`);
                        const masters = res?.data?.masters ?? [];

                        const mapped = masters.map((m: any) => ({
                            ...(slug === "speciality"
                                ? { id: m.id, slug: m.slug, value: m.slug || m.name || "" }
                                : { value: m.name || "" }),
                            label: m.name || "Unnamed",
                        }));

                        setter(mapped.sort((a: any, b: any) => a.label.localeCompare(b.label)));
                    })
                );

                console.log("✅ Masters loaded:", results);
            } catch (err) {
                console.error("❌ Failed to fetch master data:", err);
            }
        };
        fetchMasters();
    }, []);



    const verifyIFSC = async () => {
        const ifsc = (formData.bank_ifsc as string)?.toUpperCase().trim();
        const bank = (formData.bank_name as string)?.trim();

        if (!bank) return setErrors((p) => ({ ...p, bank_name: "Please select a bank first." }));
        if (!ifsc) return;

        setIfscLoading(true);
        try {
            const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
            if (!res.ok) throw new Error();

            const data = await res.json();
            const apiBank = (data.BANK || "").toLowerCase();
            const selected = bank.toLowerCase();

            if (!apiBank.includes(selected)) {
                setIfscDetails(null);
                setErrors((p) => ({
                    ...p,
                    bank_ifsc: `The IFSC belongs to ${data.BANK}, not ${bank}.`,
                }));
                return setIsBankVerified(false), setIsBankDisabled(false);
            }

            // ✅ Verified successfully
            setIfscDetails(data);
            setErrors((p) => ({ ...p, bank_ifsc: "" }));
            setIsBankVerified(true);
            setIsBankDisabled(true);
        } catch {
            setIfscDetails(null);
            setIsBankVerified(false);
            setIsBankDisabled(false);
            setErrors((p) => ({ ...p, bank_ifsc: "Invalid or unrecognized IFSC code." }));
        } finally {
            setIfscLoading(false);
        }
    };

    useEffect(() => {
        if (mode !== "edit" || !editData) return;
        console.log("🟢 EDIT DATA (raw):", JSON.stringify(editData, null, 2));
        const user = editData.user || {};
        const info = editData.additional_info || {};

        const providerSpeciality = Array.isArray(editData.provider_speciality)
            ? editData.provider_speciality.map((s: any) => s.speciality)
            : [];

        const capitalizeFirst = (str: string) =>
            str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

        setFormData((prev) => ({
            ...prev,
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            email: user.email || "",
            mobile: user.mobile || "",
            username: user.username || "",
            gender: capitalizeFirst(user.gender || ""), // ✅ Capitalize gender
            dob: user.dob || "",
            blood_group: user.blood_group || "",
            license_no: editData.license_no || info.license_no || "",
            qualification: info.qualification || "",
            aadhar_no: info.aadhar_no || "",
            practising_since: info.practising_since || "",
            bank_account_number: info.bank_account_number || "",
            bank_name: info.bank_name || "",
            bank_ifsc: info.bank_ifsc || "",
            speciality: providerSpeciality || [],
            isd_code: user.isd_code || "+91",
        }));
    }, [editData, mode]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | SelectChangeEvent<string | number>
    ) => {
        const { name, value } = e.target as HTMLInputElement | HTMLSelectElement;

        setFormData((p) => ({ ...p, [name]: value }));
        setErrors((p) => ({ ...p, [name]: "" }));

        if (name === "bank_ifsc" && !value.trim()) {
            setIsBankDisabled(false);
            setIsBankVerified(false);
            setIfscDetails(null);
        }
    };
    const resetForm = (close = false) => {
        setFormData(initialFormState);
        setErrors({});
        if (close) onClose();
    };
    const validate = () => {
        const newErrors: Record<string, string> = {};

        // Always required fields
        const requiredFields = ["firstName", "username"];
        requiredFields.forEach((key) => {
            const value = formData[key];
            if (!value || (typeof value === "string" && !value.trim())) {
                newErrors[key] = key.replace(/([A-Z])/g, " $1").trim() + " is required.";
            }
        });

        // Password & confirmPassword only in add mode
        if (mode === "add") {
            const password = formData.password;
            const confirmPassword = formData.confirmPassword;

            if (!password || !password) {
                newErrors.password = "Password is required.";
            }

            if (!confirmPassword || !confirmPassword) {
                newErrors.confirmPassword = "Confirm Password is required.";
            }

            if (password && confirmPassword && password !== confirmPassword) {
                newErrors.confirmPassword = "Passwords do not match.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };





    const handleSave = async () => {
        console.log('jgujguj')
        console.log(validate)
        if (!validate()) return;

        try {
            const isEdit = mode === "edit";
            const formattedDob = formData.dob ? moment(formData.dob).format("YYYY-MM-DD") : null;
            const formattedPractisingSince = formData.practising_since
                ? moment(formData.practising_since).format("YYYY-MM-DD")
                : null;

            // Format gender
            const formatGender = (g: string | string[]) => {
                const val = Array.isArray(g) ? g[0] : g;
                return val ? val.toLowerCase() : null;
            };

            const providerSpeciality = (Array.isArray(formData.speciality) ? formData.speciality : [])
                .map((selected: any) => {
                    const found = specialityOptions.find(
                        (opt: any) =>
                            opt.label === selected ||
                            opt.value === selected ||
                            opt.slug === selected ||
                            opt.id?.toString() === selected
                    );
                    return found
                        ? {
                            id: found.id,
                            slug: found.slug,
                            speciality: found.label || selected
                        }
                        : null;
                })
                .filter(Boolean);

            // Bank Name Label
            const selectedBankFullName =
                bankOptions.find((b) => b.value === formData.bank_name)?.label || formData.bank_name || "";
            console.log('gugugug')
            // Additional Info Object
            const additionalInfo = {
                aadhar_no: formData.aadhar_no || "",
                bank_ifsc: formData.bank_ifsc || "",
                bank_name: selectedBankFullName,
                aadhar_url: editData?.additional_info?.aadhar_url || "",
                avatar_url: editData?.additional_info?.avatar_url || "",
                license_no: formData.license_no || "",
                license_url: editData?.additional_info?.license_url || "",
                qualification: formData.qualification || "",
                practising_since: formattedPractisingSince,
                bank_account_number: editData?.additional_info?.bank_account_number || "",
            };

            const userPayload = {
                id: isEdit ? editData?.user?.id : undefined,
                role_id: 4,
                first_name: formData.firstName || "",
                last_name: formData.lastName || "",
                email: formData.email || "",
                isd_code: formData.isd_code || "+91",
                mobile: formData.mobile || "",
                ...(mode === "add" && { password: formData?.password }),
                username: formData.username || "",
                secret: isEdit ? editData?.user?.secret : undefined,
                profile_image: editData?.user?.avatar_url || "",
                gender: formatGender(formData.gender),
                dob: formattedDob,
                blood_group: formData.blood_group || "",
                ...(mode === "add"
                    ? { timezone_id: 1 } // POST requires just the foreign key
                    : { timezone: { id: 1, country_code: "IN", zone_name: "Asia/Kolkata" } } // PATCH requires full object
                ),
                address: [],
                role: "veterinerian",
                country_iso: null,
                emergency_contact_info: null,
                is_2fa: editData?.user?.is_2fa,
                is_active: editData?.user?.is_active ||1,
                activated_at: isEdit ? editData?.user?.activated_at : moment().toISOString(),
                last_activated_at: isEdit ? editData?.user?.last_activated_at : moment().toISOString(),
                fcm_tokens: isEdit ? editData?.user?.fcm_tokens || [] : [],
                communication_channel: {
                    sms: editData?.user?.communication_channel?.sms,
                    email: editData?.user?.communication_channel?.email,
                    phone: editData?.user?.communication_channel?.phone,
                },
            };

            const payload = {
                id: isEdit ? editData.id : undefined,
                user_id: isEdit ? editData.user_id || editData?.user?.id : undefined,
                primary_id: null,
                hospital_id: null,
                group_id: null,
                practicing_since: formattedPractisingSince,
                license_no: formData.license_no || "",
                specialities: null,
                additional_info: additionalInfo,
                provider_speciality: providerSpeciality,
                availabilities: null,
                unavailabilities: [],
                advance_available: [],
                average_rating: isEdit ? editData.average_rating || 0 : 0,
                review_count: isEdit ? editData.review_count || 0 : 0,
                user: userPayload,
            };

            console.log("🧾 Final Payload:", JSON.stringify(payload, null, 2));
            let response: any;
            if (isEdit) {
                response = await apiClient(`/v1/resource/provider/${editData.id}`, {
                    method: "PATCH",
                    body: payload,
                });
            } else {
                 response = await apiClient(`/v1/users/register`, {
                    method: "POST",
                    body: payload,
                });
            }
            console.log(response)
            // ✅ Success
            if (response?.code === 200 || response?.code === 201||response?.data?.code === 200) {
                alert(
                    isEdit
                        ? "Veterinarian updated successfully!"
                        : "Veterinarian registered successfully!"
                );
                resetForm(true);
                onSuccess?.();
            } else {
                throw new Error("Unexpected API response");
            }
        } catch (error: any) {
            console.error("❌ handleSave error:", error);
            alert(error?.response?.response?.data?.message || error?.message || "Something went wrong.");
        }
    };


    const makeField = (name: string, extras: any = {}) => ({
        label: name
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .trim(),
        name,
        type: "text",
        ...extras,
    });


    const fields = [
        makeField("firstName"),
        makeField("lastName"),
        makeField("gender", { type: "select", options: ["Male", "Female", "Other"] }),
        makeField("isd_code", {
            type: "select",
            options: [
                { label: "+91 (India)", value: "+91" },
                { label: "+1 (USA)", value: "+1" },
                { label: "+44 (UK)", value: "+44" },
                { label: "+61 (Australia)", value: "+61" },
                { label: "+971 (UAE)", value: "+971" },
                { label: "+81 (Japan)", value: "+81" },
            ],
        }),

        makeField("mobile", { type: "tel", placeholder: "Enter mobile number" }),
        makeField("email", { type: "email", placeholder: "Enter email address" }),
        makeField("username"),
        makeField("password", { isPassword: true }),
        makeField("confirmPassword", { isPassword: true }),

        makeField("qualification"),
        makeField("license_no"),
        makeField("aadhar_no"),
        makeField("dob", { type: "date" }),
        makeField("blood_group", {
            type: "select",
            options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
        }),
        makeField("practising_since", { type: "date" }),
        makeField("speciality", { type: "multiselect", options: specialityOptions }),

        makeField("bank_name", { type: "select", options: bankOptions }),
        makeField("bank_account_number", {
            placeholder: "Enter account number",
        }),
        makeField("bank_ifsc", {
            placeholder: "Enter IFSC code (e.g. HDFC0001234)",
        }),
    ];


    const actions = [
        { label: "Cancel", onClick: () => resetForm(true), color: "red" },
        { label: mode === "edit" ? "Update" : "Save", onClick: handleSave, color: "green" },
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-lg mx-auto">
                <h2 className="text-xl font-semibold mb-6 text-center border-b pb-2 text-gray-800">
                    {mode === "edit" ? "Edit Veterinarian" : "Add Veterinarian"}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {fields.map((f) => {
                        if (mode === "edit" && (f.name === "password" || f.name === "confirmPassword" || f.name === "bank_name" || f.name === "bank_ifsc" ||
                            f.name === "bank_account_number")) {
                            return null;
                        }
                        const value = Array.isArray(formData[f.name])
                            ? (formData[f.name] as string[])[0] ?? ""
                            : (formData[f.name] as string) ?? "";

                        const commonProps = {
                            key: f.name,
                            ...f,
                            value,
                            error: errors[f.name],
                            onChange: handleChange,
                        };

                        if (f.type === "select")
                            return (
                                <SelectField
                                    {...commonProps}
                                    disabled={f.name === "bank_name" && isBankDisabled}
                                />
                            );

                        if (f.type === "multiselect")
                            return (
                                <MultiSelectField
                                    {...commonProps}
                                    options={(f.options || []).map((opt: any) =>
                                        typeof opt === "string" ? { label: opt, value: opt } : opt
                                    )}
                                    value={(formData[f.name] as string[]) || []}
                                    onChange={(name, values) =>
                                        setFormData((p) => ({ ...p, [name]: values }))
                                    }
                                />
                            );

                        if (f.name === "bank_ifsc")
                            return (
                                <div key={f.name} className="flex flex-col md:col-span-3">
                                    <label className="text-sm font-medium text-gray-700 mb-1">
                                        {f.label}
                                    </label>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            {...f}
                                            type="text"
                                            value={value}
                                            onChange={handleChange}
                                            disabled={isBankVerified}
                                            className={`flex-1 min-w-[200px] border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 ${errors[f.name]
                                                ? "border-red-500 focus:ring-red-400"
                                                : "border-gray-300 focus:ring-blue-400"
                                                }`}
                                        />
                                        <button
                                            type="button"
                                            onClick={verifyIFSC}
                                            disabled={!formData.bank_ifsc || isBankVerified || ifscLoading}
                                            className={`px-3 py-2 rounded-md text-sm font-medium ${isBankVerified
                                                ? "bg-green-600 text-white cursor-not-allowed"
                                                : "bg-blue-600 text-white hover:bg-blue-700"
                                                }`}
                                        >
                                            {ifscLoading
                                                ? "Verifying..."
                                                : isBankVerified
                                                    ? "Verified"
                                                    : "Verify"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData((p) => ({ ...p, bank_ifsc: "" }));
                                                setIsBankDisabled(false);
                                                setIsBankVerified(false);
                                                setIfscDetails(null);
                                            }}
                                            className="px-3 py-2 rounded-md text-sm font-medium bg-gray-300 hover:bg-gray-400 text-gray-800"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    {errors[f.name] && (
                                        <p className="text-xs text-red-600 mt-1">{errors[f.name]}</p>
                                    )}
                                </div>
                            );

                        return <InputField {...commonProps} />;
                    })}
                </div>

                {ifscDetails && isBankVerified && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                        ✅ Verified {ifscDetails.BANK} – {ifscDetails.BRANCH}, {ifscDetails.ADDRESS}
                    </div>
                )}

                {ifscLoading && <p className="text-gray-500 mt-2 text-sm">Verifying IFSC...</p>}
                <div className="border-t pt-4 mt-6 flex justify-center gap-3">
                    {actions.map(({ label, onClick, color }) => (
                        <button
                            key={label}
                            onClick={onClick}
                            className={`px-5 py-2 bg-${color}-600 text-white rounded-md text-sm font-medium hover:bg-${color}-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}

                        >
                            {label}
                        </button>

                    ))}
                </div>
            </div>
        </div>
    );
}